/**
 * Transfer Action Creators
 */
import { Kushki } from "@kushki/js";
import { EnvironmentEnum } from "@kushki/js/lib/infrastructure/EnvironmentEnum";
import { BankListResponse } from "@kushki/js/lib/types/bank_list_response";
import { ErrorResponse } from "@kushki/js/lib/types/error_response";
import { MerchantSettingsResponse } from "@kushki/js/lib/types/merchant_settings_response";
import { TransferTokenRequest } from "@kushki/js/lib/types/remote/transfer_token_request";
import { setErrorMessage } from "components/Error/store/Actions";
import {
  hideOverlay,
  showOverlay,
} from "components/OverLay/store/ActionCreators";
import { Actions } from "containers/Transfer/store/Actions";
import { get } from "lodash";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { environment } from "root/environments/environment";
import { ProcessorNameEnum } from "root/shared/ProcessorNameEnum";
import {
  CajitaStore,
  IInputProps,
  IRadioGroupProps,
  ISelectItem,
  ISelectProps,
  ITransferFormProps,
} from "root/store/CajitaStore";
import { updateDisableButton } from "root/store/Reducer";
import { TokenResponse } from "types/remote/token_response";
import { ITransferActionCreator } from "types/transfer_action_creator";

function getStatusValidity(state: ITransferFormProps): boolean {
  let isValid: boolean =
    state.transferType === "Chile" ||
    state.transferType === "Mexico" ||
    state.transferType === "Ecuador" ||
    state.transferType === "Peru"
      ? true
      : state.bankListSelect.value !== "0";
  const inputs: string[] = ["paymentDescriptionInput", "documentEmailInput"];

  if (state.transferType !== "Mexico") inputs.push("documentNumberInput");

  for (const inputName of inputs) isValid = state[inputName].valid && isValid;

  return isValid;
}

const inputAction: (payload: IInputProps) => ITransferActionCreator = (
  payload: IInputProps
): ITransferActionCreator => ({
  type: Actions.UPDATE_TRANSFER_INPUT,
  input: payload,
});
const selectAction: (payload: ISelectProps) => ITransferActionCreator = (
  payload: ISelectProps
): ITransferActionCreator => ({
  type: Actions.UPDATE_SELECT_VALUE,
  select: payload,
});

const setTransferChile: (payload?: string) => ITransferActionCreator = (
  payload?: string
): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_CHILE,
  processorName: payload,
});

const setTransferMexico: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_MEXICO,
});

const setTransferPeru: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_PERU,
});

const setTransferColombia: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_COLOMBIA,
});

const setTransferEcuador: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_ECUADOR,
});

const disableTransferAction: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.DISABLE_TRANSFER_FORM,
});

const changeStatus: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SWITCH_TRANSFER_VALIDITY,
});

const updateRadioGroup: (
  payload: IRadioGroupProps
) => ITransferActionCreator = (
  payload: IRadioGroupProps
): ITransferActionCreator => ({
  type: Actions.UPDATE_RADIO_GROUP,
  radioGroup: payload,
});

const setDirty: (payload: IInputProps) => ITransferActionCreator = (
  payload: IInputProps
): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_INPUT_DIRTY,
  input: payload,
});

const tokenAction: (payload: string) => ITransferActionCreator = (
  payload: string
): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_TOKEN,
  token: payload,
});

const setSelectItemsAction: (
  payload: {
    id: string;
    items: ISelectItem[];
  }
) => ITransferActionCreator = (payload: {
  id: string;
  items: ISelectItem[];
}): ITransferActionCreator => ({
  type: Actions.SET_BANK_LIST_ITEMS,
  selectItems: payload,
});

function updateInput(
  payload: IInputProps
): ThunkAction<void, CajitaStore, undefined, ITransferActionCreator> {
  return (
    dispatch: ThunkDispatch<CajitaStore, undefined, ITransferActionCreator>,
    getState: () => CajitaStore
  ): void => {
    dispatch(inputAction(payload));
    const state: ITransferFormProps = getState().transfer;
    const status: boolean = getStatusValidity(state);

    if (state.isValid !== status) dispatch(changeStatus());
  };
}

function updateSelectValue(
  payload: ISelectProps
): ThunkAction<void, CajitaStore, undefined, ITransferActionCreator> {
  return (
    dispatch: ThunkDispatch<CajitaStore, undefined, ITransferActionCreator>,
    getState: () => CajitaStore
  ): void => {
    dispatch(selectAction(payload));
    const state: ITransferFormProps = getState().transfer;
    const status: boolean = getStatusValidity(state);

    if (state.isValid !== status) dispatch(changeStatus());
  };
}

function getTransferToken(
  body: TransferTokenRequest
): ThunkAction<void, CajitaStore, undefined, ITransferActionCreator> {
  return (dispatch: Function, getState: () => CajitaStore): void => {
    const kushki: Kushki = getState().config.kushkiJS as Kushki;

    if (body.email !== undefined && body.email.toString().length === 0)
      delete body.email;

    if (
      body.documentNumber !== undefined &&
      body.documentNumber.toString().length === 0
    ) {
      delete body.documentNumber;
      delete body.documentType;
    }

    dispatch(updateDisableButton(true));
    dispatch(showOverlay());
    kushki.requestTransferToken(
      body,
      (response: TokenResponse | ErrorResponse) => {
        if ("token" in response) dispatch(tokenAction(response.token));
        else {
          dispatch(setErrorMessage(response.message));
          dispatch(hideOverlay());
        }
        dispatch(updateDisableButton(false));
      }
    );
  };
}

function _getUrl(isTest: boolean, regional: boolean): string {
  let url: string = EnvironmentEnum.uat;

  switch (true) {
    case isTest && regional:
      url = EnvironmentEnum.regionalUat;
      break;
    case isTest && !regional:
      url = EnvironmentEnum.uat;
      break;
    case !isTest && regional:
      url = EnvironmentEnum.regionalProd;
      break;
    case !isTest && !regional:
      url = EnvironmentEnum.prod;
      break;
    default:
      break;
  }

  return url;
}

// tslint:disable-next-line:cognitive-complexity
function getBankList(
  id: string
): ThunkAction<void, CajitaStore, undefined, ITransferActionCreator> {
  // tslint:disable-next-line:max-func-body-length
  return (
    dispatch: ThunkDispatch<CajitaStore, undefined, ITransferActionCreator>,
    getState: () => CajitaStore
  ): void => {
    const kushki: Kushki = getState().config.kushkiJS as Kushki;
    const isTest: boolean =
      environment.env.toLowerCase() === "test" ||
      environment.env.toLowerCase() === "dev";
    const regional: boolean = getState().config.regional === "true";
    const url: string = _getUrl(isTest, regional);

    fetch(`${url}merchant/v1/merchant/settings`, {
      method: "GET",
      mode: "cors",
      headers: {
        "public-merchant-id": getState().config.merchant_id,
      },
    })
      .then(async (response: Response) => response.json())
      .then(async (data: MerchantSettingsResponse) => {
        function _getBankList(): void {
          const items: { value: string; text: string }[] = [];

          if (getState().config.currency === "CLP")
            items.push({ value: "0", text: "" });

          kushki.requestPseBankList(
            (response: BankListResponse | ErrorResponse) => {
              if (Array.isArray(response))
                dispatch(
                  setSelectItemsAction({
                    id,
                    items: items.concat(
                      response.map((bank: { code: string; name: string }) => ({
                        value: bank.code,
                        text: bank.name,
                      }))
                    ),
                  })
                );
              else if (getState().transfer.isTransferAvailable)
                dispatch(disableTransferAction());

              return;
            }
          );
        }

        if (getState().config.currency === "CLP") {
          const etpay: { processorName: string } | undefined = get(
            data,
            "processors.transfer",
            []
          ).find(
            (processor: { processorName: string }) =>
              processor.processorName === ProcessorNameEnum.ETPAYPLUS
          );

          if (etpay !== undefined) {
            dispatch(setTransferChile(etpay.processorName));
            _getBankList();

            return;
          }

          dispatch(setTransferChile());

          return;
        }
        if (getState().config.currency === "MXN") {
          dispatch(setTransferMexico());

          return;
        }
        if (getState().config.currency === "PEN") {
          dispatch(setTransferPeru());

          return;
        }
        if (getState().config.currency === "USD") {
          dispatch(setTransferEcuador());

          return;
        }

        if (getState().config.currency !== "COP") return;
        dispatch(setTransferColombia());
        _getBankList();
      })
      .catch(() => dispatch(disableTransferAction()));
  };
}

export {
  setDirty,
  updateInput,
  updateSelectValue,
  updateRadioGroup,
  getTransferToken,
  getBankList,
  changeStatus,
  disableTransferAction,
  setTransferMexico,
  setTransferColombia,
};



// WEBPACK FOOTER //
// ./src/containers/Transfer/store/ActionCreators.tsx